import React, { useEffect, useRef, useState } from 'react';
import { Dialog } from 'primereact/dialog';
import { Button } from 'primereact/button';
import { Message } from 'primereact/message';
import { Tag } from 'primereact/tag';
import { useEsMovil } from '../../indicadores/hooks/useEsMovil';
import { ErrorSinRespuesta, MetasColaboradorService } from '../services/metasColaborador.service';
import type { Aviso, CambioImportacion, ResultadoImportacion, TiendaImportacion } from '../types/metas.types';
import { etiquetaEstado, severidadEstado } from '../utils/estado';
import { etiquetaSemana } from '../utils/semanas';
import { AvisosLista } from './AvisosLista';
import { CLASE_OVERLAY } from './ui/clases';

interface Props {
  /** El Excel que se eligió; `null` = diálogo cerrado. */
  archivo: File | null;
  /** La semana en pantalla cuando se eligió el archivo. */
  lunes: string;
  onHide: () => void;
  /** Tras guardar: la captura recarga la tienda abierta y la lista de tiendas. */
  onAplicado: (r: ResultadoImportacion) => void;
  /** Mientras valida o guarda: la captura bloquea la tienda y la semana. */
  onOcupado?: (ocupado: boolean) => void;
  /** Guardar se cortó SIN respuesta: pudo haberse guardado; la captura recarga. */
  onIncierto?: () => void;
}

const llave = (t: { chain: string; num: number }) => `${t.chain}|${t.num}`;

const nombreTienda = (t: TiendaImportacion) =>
  t.chain ? `${t.chain} ${t.num}${t.tienda ? ` · ${t.tienda}` : ''}` : 'Renglones sin tienda';

/** Las tiendas que «Guardar» guardaría: sin errores y con algo que cambiar (la API salta las demás). */
export const guardables = (r: ResultadoImportacion | null): TiendaImportacion[] =>
  (r?.tiendas ?? []).filter((t) => t.errores.length === 0 && t.cambios.length > 0);

const avisosDe = (t: TiendaImportacion): Aviso[] => [
  ...t.errores.map((mensaje) => ({ codigo: 'EXCEL_ERROR', nivel: 'error' as const, mensaje })),
  ...t.advertencias.map((mensaje) => ({ codigo: 'EXCEL_AVISO', nivel: 'advertencia' as const, mensaje })),
];

/**
 * Vista previa y carga de un Excel de metas.
 *
 * El archivo se VALIDA primero (la API arma la plantilla de cada tienda y dice
 * qué cambia, sin guardar) y sólo se guarda cuando el supervisor lo confirma,
 * con el MISMO archivo. La API guarda todo o nada por tienda: una tienda con un
 * error se queda como estaba y las demás sí se guardan. Por eso cada tienda se
 * pinta por separado, con sus errores en rojo y el aviso de que no se guardará.
 *
 * ⚠️ Sólo cuenta la respuesta del ÚLTIMO archivo (contador `pedido`): cerrar y
 * subir otro antes de que el primero respondiera pintaba la vista previa del
 * primero, y «Guardar» mandaba el segundo.
 *
 * ⚠️ Mientras guarda no se deja cerrar: la respuesta es lo único que dice qué
 * tiendas se guardaron y cuáles no.
 */
export const ImportarExcelDialog: React.FC<Props> = ({ archivo, lunes, onHide, onAplicado, onOcupado, onIncierto }) => {
  const esMovil = useEsMovil(760);
  const [vistaPrevia, setVistaPrevia] = useState<ResultadoImportacion | null>(null);
  const [resultado, setResultado] = useState<ResultadoImportacion | null>(null);
  const [fase, setFase] = useState<null | 'validando' | 'aplicando'>(null);
  const [error, setError] = useState<string | null>(null);

  const pedido = useRef(0);
  useEffect(() => {
    const n = ++pedido.current;
    setVistaPrevia(null);
    setResultado(null);
    setError(null);
    if (!archivo) {
      setFase(null);
      return;
    }
    setFase('validando');
    MetasColaboradorService.validarExcel(lunes, archivo)
      .then((r) => {
        if (n === pedido.current) setVistaPrevia(r);
      })
      .catch((e: unknown) => {
        if (n === pedido.current) setError((e as Error)?.message ?? String(e));
      })
      .finally(() => {
        if (n === pedido.current) setFase(null);
      });
  }, [archivo, lunes]);

  useEffect(() => {
    onOcupado?.(fase !== null);
  }, [fase, onOcupado]);

  const porGuardar = guardables(vistaPrevia);

  const aplicar = async () => {
    // El archivo y la semana son los de la vista previa en pantalla: se guarda
    // exactamente lo que se revisó.
    if (!archivo || !vistaPrevia || !porGuardar.length || fase) return;
    const n = pedido.current;
    setFase('aplicando');
    setError(null);
    try {
      const r = await MetasColaboradorService.importarExcel(vistaPrevia.lunes, archivo, porGuardar);
      // Se guardó, esté o no abierto este diálogo: la captura tiene que recargar.
      onAplicado(r);
      if (n === pedido.current) setResultado(r);
    } catch (e) {
      // Sin respuesta HTTP no se sabe si la API guardó: la captura recarga igual,
      // o su siguiente «Guardar borrador» iría con la versión vieja (409).
      if (e instanceof ErrorSinRespuesta) onIncierto?.();
      if (n === pedido.current) setError((e as Error)?.message ?? String(e));
    } finally {
      if (n === pedido.current) setFase(null);
    }
  };

  const cerrar = () => {
    if (fase === 'aplicando') return;
    onHide();
  };

  const mostrado = resultado ?? vistaPrevia;
  const conErrores = mostrado ? mostrado.tiendas.filter((t) => t.errores.length > 0).length : 0;

  const pie = (
    <div className="flex flex-wrap justify-end gap-2">
      {resultado ? (
        <Button label="Cerrar" onClick={cerrar} />
      ) : (
        <>
          <Button label="Cancelar" text onClick={cerrar} disabled={fase === 'aplicando'} />
          {vistaPrevia && (
            <Button
              label={`Guardar ${porGuardar.length} tienda(s) sin errores`}
              icon="pi pi-save"
              severity="success"
              disabled={!porGuardar.length || fase !== null}
              loading={fase === 'aplicando'}
              onClick={() => void aplicar()}
            />
          )}
        </>
      )}
    </div>
  );

  const cambiosDe = (cambios: CambioImportacion[]) =>
    esMovil ? (
      <ul className="flex flex-col gap-2" data-testid="excel-cambios">
        {cambios.map((c, i) => (
          <li key={`${c.nombre}-${i}`} className="rounded border border-gray-200 px-3 py-2 text-sm">
            <div className="font-medium">{c.nombre}</div>
            <div className="break-words text-gray-600">
              {c.antes} <i className="pi pi-arrow-right mx-1 text-xs" aria-label="pasa a" /> <strong>{c.despues}</strong>
            </div>
          </li>
        ))}
      </ul>
    ) : (
      <table className="w-full text-left text-sm" data-testid="excel-cambios">
        <thead>
          <tr className="border-b border-gray-200 text-gray-600">
            <th className="py-1 pr-3 font-semibold">Persona</th>
            <th className="py-1 pr-3 font-semibold">Antes</th>
            <th className="py-1 font-semibold">Después</th>
          </tr>
        </thead>
        <tbody>
          {cambios.map((c, i) => (
            <tr key={`${c.nombre}-${i}`} className="border-b border-gray-100 align-top">
              <td className="py-1 pr-3 font-medium">{c.nombre}</td>
              <td className="py-1 pr-3 text-gray-600">{c.antes}</td>
              <td className="py-1 font-semibold">{c.despues}</td>
            </tr>
          ))}
        </tbody>
      </table>
    );

  /** Qué le pasa (o le pasó) a la tienda con este archivo. */
  const destino = (t: TiendaImportacion) => {
    if (resultado) {
      if (t.guardada)
        return (
          <Tag
            value={`Guardada${t.estadoNuevo ? ` · queda ${etiquetaEstado(t.estadoNuevo).toLowerCase()}` : ''}`}
            severity="success"
          />
        );
      if (t.errores.length) return <Tag value="No se guardó" severity="danger" />;
      return <Tag value="Sin cambios" severity="secondary" />;
    }
    if (t.errores.length) return <Tag value="No se guardará" severity="danger" />;
    if (!t.cambios.length) return <Tag value="Sin cambios" severity="secondary" />;
    return <Tag value={`${t.cambios.length} cambio(s)`} severity="info" />;
  };

  // Una tienda enviada o autorizada ya no «regresa a borrador» al guardar
  // (decisión del 29-sep: son de sólo lectura): la API la marca con error y no
  // se guarda. No hay nada que avisar aparte.
  const bloqueTienda = (t: TiendaImportacion) => {
    return (
      <li
        key={llave(t)}
        className={`flex flex-col gap-2 rounded-lg border p-3 ${t.errores.length ? 'border-red-300 bg-red-50/40' : 'border-gray-200 bg-white'}`}
        data-testid={`excel-tienda-${t.chain}-${t.num}`}
      >
        <div className="flex flex-wrap items-center gap-2">
          <strong className="text-gray-800">{nombreTienda(t)}</strong>
          <Tag value={etiquetaEstado(t.estado)} severity={severidadEstado(t.estado)} />
          {destino(t)}
        </div>
        {(t.errores.length > 0 || t.advertencias.length > 0) && <AvisosLista avisos={avisosDe(t)} />}
        {t.cambios.length > 0 && cambiosDe(t.cambios)}
      </li>
    );
  };

  return (
    <Dialog
      // La ✕ en español: el locale global de PrimeReact sigue en `en` («Close»).
      ariaCloseIconLabel="Cerrar"
      className={CLASE_OVERLAY}
      header={`Subir Excel — ${etiquetaSemana(lunes)}`}
      visible={!!archivo}
      onHide={cerrar}
      closable={fase !== 'aplicando'}
      footer={pie}
      // Igual que la revisión del regional: en teléfono, a pantalla completa y
      // controlado (`onMaximize`), para que al girarlo no se quede a medias.
      maximized={esMovil}
      onMaximize={() => undefined}
      style={esMovil ? { width: '100vw', maxWidth: '100vw', margin: 0 } : { width: '95vw', maxWidth: 1000 }}
      modal
    >
      <div className="flex flex-col gap-3" data-testid="importar-excel">
        {archivo && <div className="break-all text-sm text-gray-500">Archivo: {archivo.name}</div>}

        {fase === 'validando' && (
          <div className="py-4 text-gray-500">
            Revisando el archivo… La API arma la plantilla de cada tienda: con varias tarda unos segundos.
          </div>
        )}

        {error && (
          <div role="alert">
            <Message severity="error" text={error} className="w-full justify-start" />
          </div>
        )}

        {mostrado && (
          <>
            <div className="flex flex-col gap-2 rounded-lg border border-gray-200 bg-gray-50 p-3" data-testid="excel-resumen">
              {resultado ? (
                <strong className="text-gray-800">
                  Se guardaron {resultado.resumen.guardadas} de {resultado.resumen.tiendas} tienda(s).
                </strong>
              ) : (
                <strong className="text-gray-800">Vista previa: todavía no se guarda nada.</strong>
              )}
              <div className="flex flex-wrap gap-2 text-sm">
                <Tag value={`${mostrado.resumen.tiendas} tienda(s) en el archivo`} severity="secondary" />
                <Tag value={`${mostrado.resumen.cambios} cambio(s)`} severity="info" />
                {conErrores > 0 && (
                  <Tag
                    value={`${conErrores} con errores: ${resultado ? 'no se guardaron' : 'no se guardarán'}`}
                    severity="danger"
                  />
                )}
              </div>
              {!resultado && (
                <div className="text-sm text-gray-600">
                  Cada tienda se guarda completa o no se guarda: la que tiene un error se queda como está y las demás sí
                  se guardan.
                </div>
              )}
              {!resultado && !porGuardar.length && (
                <div className="text-sm font-semibold text-red-800">
                  {conErrores
                    ? 'No hay ninguna tienda que se pueda guardar: corrige los errores en el archivo y vuelve a subirlo.'
                    : 'El archivo no cambia nada de lo que ya está guardado.'}
                </div>
              )}
            </div>
            <ul className="flex flex-col gap-3">{mostrado.tiendas.map(bloqueTienda)}</ul>
          </>
        )}
      </div>
    </Dialog>
  );
};

export default ImportarExcelDialog;
